/**
 * EquippedDiceTryController — "try" the player's equipped loadout from the Dice
 * library. Extends DiceTryScene (the shared isolated shoot scene) and adds a
 * 2 / 3 dice toggle so the player can feel their combo for both modes:
 *   - 2 DICE → Street Craps (loadout slots 0,1)
 *   - 3 DICE → Cee-Lo       (loadout slots 0,1,2)
 *
 * The close (X) from the base returns to the Dice screen.
 */

import { DiceTryScene, type DiceTrySceneDeps } from './DiceTryScene';

const STYLE_ID = 'sdl-equiptry-styles';

export interface EquippedDiceTryDeps extends DiceTrySceneDeps {
  /** The active preset's loadout slots (craps, craps, cee-lo). */
  slots: string[];
}

export class EquippedDiceTryController extends DiceTryScene {
  private slots: string[];
  private mode: 2 | 3;
  private toggleEl: HTMLElement | null = null;

  constructor(deps: EquippedDiceTryDeps) {
    super(deps);
    this.slots = deps.slots.filter(Boolean);
    // Default to the fuller combo when the loadout has 3 dice.
    this.mode = this.slots.length >= 3 ? 3 : 2;
  }

  async start(): Promise<void> {
    if (this.active || this.slots.length === 0) return;
    this.active = true;

    await this.preload(this.slots);
    if (!this.active) return;

    await this.buildScene();
    this.buildToggle();
    this.buildCloseButton();
    this.spawnForMode();

    this.engine.start();
  }

  /** Skins for the current mode: 2 = craps pair, 3 = cee-lo trio. */
  private currentSkins(): string[] {
    return this.slots.slice(0, this.mode);
  }

  private spawnForMode(): void {
    this.spawnDice(this.currentSkins());
  }

  private setMode(mode: 2 | 3): void {
    if (this.mode === mode) return;
    this.mode = mode;
    this.toggleEl?.querySelectorAll<HTMLElement>('.sdl-equiptry-opt').forEach((b) => {
      b.classList.toggle('active', Number(b.dataset.mode) === mode);
    });
    this.spawnForMode();
  }

  private buildToggle(): void {
    EquippedDiceTryController.injectToggleStyles();
    const host = document.createElement('div');
    host.className = 'sdl-equiptry-toggle';
    host.innerHTML = `
      <div class="sdl-equiptry-title">Try your dice</div>
      <div class="sdl-equiptry-opts">
        <button class="sdl-equiptry-opt" data-mode="2">
          <span class="sdl-equiptry-n">2 DICE</span><span class="sdl-equiptry-sub">Craps</span>
        </button>
        <button class="sdl-equiptry-opt" data-mode="3">
          <span class="sdl-equiptry-n">3 DICE</span><span class="sdl-equiptry-sub">Cee-Lo</span>
        </button>
      </div>
    `;
    document.body.appendChild(host);
    this.toggleEl = host;
    host.querySelectorAll<HTMLElement>('.sdl-equiptry-opt').forEach((b) => {
      const mode = Number(b.dataset.mode) as 2 | 3;
      b.classList.toggle('active', mode === this.mode);
      // A 2-die loadout can't fill the cee-lo trio — disable that option.
      if (mode === 3 && this.slots.length < 3) b.setAttribute('disabled', 'true');
      b.addEventListener('click', () => this.setMode(mode));
    });
  }

  protected disposeControls(): void {
    this.toggleEl?.remove();
    this.toggleEl = null;
  }

  private static injectToggleStyles(): void {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      .sdl-equiptry-toggle {
        position: fixed; z-index: 210;
        top: max(12px, calc(env(safe-area-inset-top) + 64px));
        left: 50%; transform: translateX(-50%);
        display: flex; flex-direction: column; align-items: center; gap: 8px;
        padding: 12px 16px;
        background: rgba(10, 10, 14, 0.92);
        border: 1px solid rgba(255, 255, 255, 0.08);
        border-radius: 16px;
        box-shadow: 0 14px 38px rgba(0, 0, 0, 0.6);
        backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px);
        animation: sdl-equiptry-in 0.24s cubic-bezier(0.34, 1.4, 0.6, 1) both;
      }
      @keyframes sdl-equiptry-in { from { transform: translate(-50%, -12px); opacity: 0; } to { transform: translate(-50%, 0); opacity: 1; } }
      .sdl-equiptry-title {
        font-family: 'Bebas Neue', sans-serif; letter-spacing: 1px;
        font-size: 18px; color: var(--gold, #FFD700);
      }
      .sdl-equiptry-opts { display: flex; gap: 10px; }
      .sdl-equiptry-opt {
        display: flex; flex-direction: column; align-items: center; gap: 2px;
        min-width: 84px; padding: 9px 16px;
        background: rgba(255, 255, 255, 0.05);
        border: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 12px;
        cursor: pointer; -webkit-tap-highlight-color: transparent;
        transition: transform 0.12s ease, background 0.15s ease, border-color 0.15s ease;
      }
      .sdl-equiptry-opt:active { transform: scale(0.95); }
      .sdl-equiptry-opt.active {
        background: linear-gradient(180deg, rgba(0, 245, 255, 0.22), rgba(0, 245, 255, 0.08));
        border-color: var(--cyan, #00F5FF);
        box-shadow: 0 0 16px rgba(0, 245, 255, 0.35);
      }
      .sdl-equiptry-opt[disabled] { opacity: 0.35; pointer-events: none; }
      .sdl-equiptry-n {
        font-family: 'Bebas Neue', sans-serif; letter-spacing: 1px;
        font-size: 18px; color: #fff;
      }
      .sdl-equiptry-sub {
        font-family: 'Rajdhani', sans-serif; font-weight: 600;
        font-size: 11px; letter-spacing: 0.5px; color: rgba(255, 255, 255, 0.6);
      }
    `;
    document.head.appendChild(style);
  }
}
